// API Service for AI Interface
class ApiService {
  constructor(baseURL = '') {
    this.baseURL = baseURL;
    this.onAuthError = null; // Callback for authentication errors
    this.isRefreshing = false; // Flag to prevent multiple simultaneous refresh attempts
    this.refreshPromise = null; // Store the refresh promise to reuse
  }

  // Set authentication error callback
  setAuthErrorCallback(callback) {
    this.onAuthError = callback;
  }

  // Check if token is expired or about to expire (within 5 minutes)
  isTokenExpired(expiryDate) {
    if (!expiryDate) return true;
    const now = Date.now();
    const expiry = new Date(expiryDate).getTime();
    const fiveMinutes = 5 * 60 * 1000;
    return expiry - now < fiveMinutes;
  }

  // Refresh access token using refresh token
  async refreshTokenIfNeeded() {
    try {
      // Get token expiry from localStorage
      const expiryDate = localStorage.getItem('ai_token_expiry');

      // If token isn't expired, no need to refresh
      if (!this.isTokenExpired(expiryDate)) {
        return true;
      }

      // If already refreshing, wait for that promise
      if (this.isRefreshing && this.refreshPromise) {
        return await this.refreshPromise;
      }

      // Mark as refreshing and create the refresh promise
      this.isRefreshing = true;
      this.refreshPromise = this._performTokenRefresh();

      const result = await this.refreshPromise;

      // Reset refresh state
      this.isRefreshing = false;
      this.refreshPromise = null;

      return result;
    } catch (error) {
      console.error('🔐 apiService: Error in refreshTokenIfNeeded:', error);
      this.isRefreshing = false;
      this.refreshPromise = null;
      return false;
    }
  }

  // Perform the actual token refresh
  async _performTokenRefresh() {
    try {
      const refreshToken = localStorage.getItem('ai_refresh_token');

      if (!refreshToken) {
        return false;
      }

      // Call refresh endpoint without authentication (to avoid infinite loop)
      const response = await fetch(`${this.baseURL}/oauth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        console.error('🔐 apiService: Token refresh failed:', response.status);
        // Clear all tokens if refresh fails
        localStorage.removeItem('ai_oauth_token');
        localStorage.removeItem('ai_refresh_token');
        localStorage.removeItem('ai_token_expiry');
        return false;
      }

      const data = await response.json();

      if (data.success && data.tokens) {
        // Update stored tokens
        localStorage.setItem('ai_oauth_token', data.tokens.access_token);
        if (data.tokens.refresh_token) {
          localStorage.setItem('ai_refresh_token', data.tokens.refresh_token);
        }
        if (data.tokens.expiry_date) {
          localStorage.setItem('ai_token_expiry', data.tokens.expiry_date);
        }

        return true;
      } else {
        console.error('🔐 apiService: Token refresh response invalid:', data);
        return false;
      }
    } catch (error) {
      console.error('🔐 apiService: Error refreshing token:', error);
      return false;
    }
  }

  // Global error handler for authentication errors
  handleAuthError(error) {
    // Check if this is an authentication error
    const isAuthError =
      error &&
      (error.message?.includes('Invalid or expired token') ||
        error.message?.includes('The provided access token is invalid or expired') ||
        error.message?.includes('Access token required') ||
        error.message?.includes('Invalid access token') ||
        error.error?.includes('Invalid access token') ||
        error.status === 401 ||
        error.status === 403 ||
        error.status === 0); // Network errors

    if (isAuthError && this.onAuthError) {
      try {
        this.onAuthError(error);
      } catch (callbackError) {
        console.error('🔐 apiService: Error in auth error callback:', callbackError);
      }
    }

    return {
      ok: false,
      error: error,
    };
  }

  // Enhanced fetch wrapper with authentication error handling
  async authenticatedFetch(url, options = {}) {
    try {
      // Check and refresh token if needed (skip for refresh endpoint to avoid infinite loop)
      if (!url.includes('/oauth/refresh')) {
        const refreshed = await this.refreshTokenIfNeeded();
        if (!refreshed) {
          // Continue anyway - the request might fail with 401 and trigger logout
        }
      }

      // Get authentication token from localStorage
      let token = null;
      try {
        const oauthToken = localStorage.getItem('ai_oauth_token');
        const authToken = localStorage.getItem('ai_auth_token');
        token = oauthToken || authToken;
      } catch (e) {
        // localStorage might not be available (e.g., in Node.js environment)
      }

      // Add Authorization header if token exists
      const headers = {
        ...options.headers,
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const fetchOptions = {
        ...options,
        headers,
      };

      const response = await fetch(url, fetchOptions);

      // Check for authentication errors
      if (response.status === 401 || response.status === 403) {
        const errorData = await response.json().catch(() => ({
          message: 'Authentication failed',
          status: response.status,
        }));
        return this.handleAuthError(errorData);
      }
      return response;
    } catch (error) {
      console.error('🔐 apiService: Network error in authenticatedFetch:', error);
      return this.handleAuthError({
        message: 'Network error',
        status: 0,
        originalError: error,
      });
    }
  }

  // Authentication
  async validateToken(token) {
    const response = await this.authenticatedFetch(`${this.baseURL}/oauth/validate-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ access_token: token }),
    });

    // If response is already an error object, return it
    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: response.ok ? await response.json() : null,
      error: !response.ok ? await response.json().catch(() => ({ message: 'Invalid or expired token' })) : null,
    };

    return result;
  }

  // OAuth Authentication
  async generateOAuthUrl() {
    const payload =
      typeof window !== 'undefined' && window.location && window.location.origin
        ? { redirectUri: `${window.location.origin}/oauth/google/callback` }
        : {};
    const response = await this.authenticatedFetch(`${this.baseURL}/oauth/google/auth-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    // If response is already an error object, return it
    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: response.ok ? await response.json() : null,
      error: !response.ok ? await response.json().catch(() => ({ message: 'Failed to generate OAuth URL' })) : null,
    };

    return result;
  }

  async handleOAuthCallback(code, state) {
    const response = await this.authenticatedFetch(
      `${this.baseURL}/oauth/google/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    // If response is already an error object, return it
    if (!response.ok && response.error) {
      return response;
    }

    const result = {
      ok: response.ok,
      data: response.ok ? await response.json() : null,
      error: !response.ok ? await response.json().catch(() => ({ message: 'Failed to handle OAuth callback' })) : null,
    };

    return result;
  }

  async refreshOAuthToken(refreshToken) {
    const response = await this.authenticatedFetch(`${this.baseURL}/oauth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });

    // If response is already an error object, return it
    if (!response.ok && response.error) {
      return response;
    }

    return {
      ok: response.ok,
      data: response.ok ? await response.json() : null,
      error: !response.ok ? await response.json().catch(() => ({ message: 'Failed to refresh token' })) : null,
    };
  }

  async getUserProfile(accessToken) {
    const response = await this.authenticatedFetch(`${this.baseURL}/oauth/user-profile`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ accessToken }),
    });

    // If response is already an error object, return it
    if (!response.ok && response.error) {
      return response;
    }

    return {
      ok: response.ok,
      data: response.ok ? await response.json() : null,
      error: !response.ok ? await response.json().catch(() => ({ message: 'Failed to get user profile' })) : null,
    };
  }

  // Local Authentication
  async localLogin(email, password) {
    const response = await fetch(`${this.baseURL}/oauth/local/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    return {
      ok: response.ok,
      data: response.ok ? await response.json() : await response.json().catch(() => null),
      error: !response.ok ? await response.json().catch(() => ({ message: 'Login failed' })) : null,
    };
  }

  async localRegister(email, password, name, companyName) {
    const response = await fetch(`${this.baseURL}/oauth/local/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password, name, companyName }),
    });

    return {
      ok: response.ok,
      data: response.ok ? await response.json() : await response.json().catch(() => null),
      error: !response.ok ? await response.json().catch(() => ({ message: 'Registration failed' })) : null,
    };
  }

  async localGetProfile(accessToken) {
    const response = await fetch(`${this.baseURL}/oauth/local/profile`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    return {
      ok: response.ok,
      data: response.ok ? await response.json() : null,
      error: !response.ok ? await response.json().catch(() => ({ message: 'Failed to get profile' })) : null,
    };
  }

  async updateUserPreferences(token, preferences) {
    const response = await this.authenticatedFetch(`${this.baseURL}/api/users/preferences`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(preferences),
    });

    if (!response.ok && response.error) {
      return response;
    }

    return {
      ok: response.ok,
      data: response.ok ? await response.json() : null,
      error: !response.ok ? await response.json().catch(() => ({ message: 'Failed to update preferences' })) : null,
    };
  }

  // Chat Session Management
  async startChatSession(token, sessionData) {
    const response = await this.authenticatedFetch(`${this.baseURL}/api/chatbot/session/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(sessionData),
    });

    // If response is already an error object, return it
    if (!response.ok && response.error) {
      return response;
    }

    return {
      ok: response.ok,
      data: response.ok ? await response.json() : null,
      error: !response.ok ? await response.json().catch(() => ({ message: 'Failed to start chat session' })) : null,
    };
  }

  async sendMessage(token, sessionId, message, options = {}) {
    const response = await this.authenticatedFetch(`${this.baseURL}/api/chatbot/session/message`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        sessionId,
        message,
        ...options,
      }),
    });

    // If response is already an error object, return it
    if (!response.ok && response.error) {
      return response;
    }

    return {
      ok: response.ok,
      data: response.ok ? await response.json() : null,
      error: !response.ok ? await response.json().catch(() => ({ message: 'Failed to send message' })) : null,
    };
  }

  async sendMessageStream(token, sessionId, message, options = {}, onChunk, onComplete, onError) {
    try {
      const response = await this.authenticatedFetch(`${this.baseURL}/api/chatbot/session/message/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          sessionId,
          message,
          ...options,
        }),
      });

      // If response is already an error object, return it
      if (!response.ok && response.error) {
        if (onError) onError(response.error);
        return response;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to start streaming' }));
        if (onError) onError(errorData);
        return {
          ok: false,
          error: errorData,
        };
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            break;
          }

          // Decode the chunk and add to buffer
          buffer += decoder.decode(value, { stream: true });

          // Process complete lines from buffer
          const lines = buffer.split('\n');
          buffer = lines.pop(); // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.trim() === '') continue;

            // Parse Server-Sent Events format
            if (line.startsWith('data: ')) {
              try {
                const jsonData = line.slice(6); // Remove 'data: ' prefix
                const chunk = JSON.parse(jsonData);

                // Handle different chunk types
                switch (chunk.type) {
                  case 'connection':
                    break;
                  case 'content':
                  case 'chunk':
                  case 'function_calls':
                  case 'function_result':
                  case 'status':
                    if (onChunk) onChunk(chunk);
                    break;
                  case 'complete':
                    if (onComplete) onComplete(chunk);
                    break;
                  case 'error':
                    console.error('API Service: Stream error:', chunk.error);
                    if (onError) onError(chunk);
                    break;
                  default:
                    if (onChunk) onChunk(chunk);
                }
              } catch (parseError) {
                console.error('API Service: Error parsing chunk:', parseError, line);
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }

      return {
        ok: true,
        data: { message: 'Stream completed' },
      };
    } catch (error) {
      console.error('API Service: Streaming error:', error);
      if (onError) onError({ error: error.message });
      return {
        ok: false,
        error: { message: error.message },
      };
    }
  }

  async getChatHistory(token, sessionId) {
    const response = await this.authenticatedFetch(`${this.baseURL}/api/chatbot/session/history/${sessionId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    // If response is already an error object, return it
    if (!response.ok && response.error) {
      return response;
    }

    return {
      ok: response.ok,
      data: response.ok ? await response.json() : null,
      error: !response.ok ? await response.json().catch(() => ({ message: 'Failed to get chat history' })) : null,
    };
  }

  async endChatSession(token, sessionId) {
    const response = await this.authenticatedFetch(`${this.baseURL}/api/chatbot/session/end`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ sessionId }),
    });

    // If response is already an error object, return it
    if (!response.ok && response.error) {
      return response;
    }

    return {
      ok: response.ok,
      data: response.ok ? await response.json() : null,
      error: !response.ok ? await response.json().catch(() => ({ message: 'Failed to end chat session' })) : null,
    };
  }

  // Settings Management
  async updatePersonality(token, personality) {
    const response = await this.authenticatedFetch(`${this.baseURL}/api/chatbot/personality`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ personality }),
    });

    // If response is already an error object, return it
    if (!response.ok && response.error) {
      return response;
    }

    return {
      ok: response.ok,
      data: response.ok ? await response.json() : null,
      error: !response.ok ? await response.json().catch(() => ({ message: 'Failed to update personality' })) : null,
    };
  }

  // MCP Tools
  async getMCPTools(token) {
    const response = await this.authenticatedFetch(`${this.baseURL}/api/mcp/tools`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    // If response is already an error object, return it
    if (!response.ok && response.error) {
      return response;
    }

    return {
      ok: response.ok,
      data: response.ok ? await response.json() : null,
      error: !response.ok ? await response.json().catch(() => ({ message: 'Failed to fetch MCP tools' })) : null,
    };
  }

  async getMCPInstances(token) {
    const response = await this.authenticatedFetch(`${this.baseURL}/api/mcp/instances`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok && response.error) {
      return response;
    }

    return {
      ok: response.ok,
      data: response.ok ? await response.json() : null,
      error: !response.ok ? await response.json().catch(() => ({ message: 'Failed to fetch MCP instances' })) : null,
    };
  }

  // Error handling
  handleError(error) {
    console.error('API Error:', error);
    return {
      ok: false,
      error: {
        message: error.message || 'An unexpected error occurred',
      },
    };
  }
}

// Create and export a singleton instance
const apiService = new ApiService();

// Export for testing
if (typeof module !== 'undefined' && module.exports) {
  module.exports = { apiService };
}
